import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Image,
  message,
  Upload,
  Input,
  Typography,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import Router from "..";
import { useUserStore } from "../../stores/userStore";
import { fileToBase64 } from "../utils/fileToBase64";
import { useLanguageStore } from "../../stores/languageStore";
import { bankList } from "../utils/bankList";

const allBankValues = bankList.flatMap((group) =>
  group.options.map((bank) => bank.value)
);

export const BankAccntForm = ({ cardClassName = "" }) => {
  const [form] = Form.useForm();

  const { userInfo, loadUserInfo } = useUserStore();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [bankAccntImg, setBankAccntImg] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const lang = useLanguageStore((state) => state.lang);

  const userType = userInfo?.user_info?.user_type;
  const isCompany = userType === "brand" || userType === "agency";
  const isInfluencer = userType === "influencer";

  // 브랜드/에이전시는 master만, 크리에이터는 모두 사용 가능
  const canEdit = isCompany
    ? userInfo?.type_info?.grade === "master"
    : isInfluencer;

  const hasBankAccount = !!userInfo?.type_info?.bank_account_url;
  const isVerified = !!userInfo?.type_info?.is_bank_verified;
  const bankInfoDisabled =
    !canEdit ||
    (hasBankAccount && !isEditMode) ||
    (!hasBankAccount && isDisabled);
  const showActionArea = canEdit && !(!hasBankAccount && isDisabled);
  const showEditButton = hasBankAccount && !isEditMode;
  const showCancelButton = !hasBankAccount || isEditMode;

  useEffect(() => {
    if (userInfo?.type_info) {
      const savedBankName = userInfo.type_info.bank_name;
      const isCustomBank =
        !!savedBankName && !allBankValues.includes(savedBankName);

      form.setFieldsValue({
        bank_holder_name: userInfo?.type_info?.bank_holder_name,
        bank_name: isCustomBank ? "custom" : savedBankName,
        custom_bank_name: isCustomBank ? savedBankName : null,
        bank_account_number: userInfo.type_info.bank_account_number,
      });
    }

    if (userInfo?.type_info?.bank_account_url) {
      setBankAccntImg(userInfo.type_info.bank_account_url);
      setIsDisabled(true);
    } else {
      setIsDisabled(true);
    }
  }, [userInfo, form]);

  const handleFileSelect = async (file) => {
    const base64 = await fileToBase64(file);
    setBankAccntImg(base64);
    setSelectedFile(file);
    return false; // 자동 업로드 방지
  };

  const handleSubmit = async (values) => {
    if (!bankAccntImg) {
      message.error("통장 사본을 업로드해주세요.");
      return;
    }

    const finalBankName =
      values.bank_name === "custom"
        ? values.custom_bank_name?.trim()
        : values.bank_name;

    const result = isCompany
      ? await Router.Api.addBankAccountByCompany(
          values.bank_holder_name,
          bankAccntImg,
          values.bank_account_number,
          finalBankName
        )
      : await Router.Api.addBankAccountByInfluencer(
          values.bank_holder_name,
          bankAccntImg,
          values.bank_account_number,
          finalBankName
        );

    if (result) {
      message.success(
        userInfo?.type_info?.bank_account_url
          ? "통장 정보가 수정되었습니다."
          : "통장 정보가 추가되었습니다."
      );
      setIsEditMode(false);
      setIsDisabled(true);
      await loadUserInfo();
      setSelectedFile(null);
    }
  };

  return (
    <div className="w-full">
      <Card
        title={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex gap-4">
              <div>{lang === "en" ? "Copy of Bank Account" : "통장 정보"}</div>
              {hasBankAccount && (
                <span
                  className={[
                    "inline-flex w-fit items-center rounded-full px-3 py-1 text-[12px] font-semibold",
                    isVerified
                      ? "bg-[#ecfdf3] text-[#027a48]"
                      : "bg-[#fff7ed] text-[#c2410c]",
                  ].join(" ")}
                >
                  {isVerified ? "인증 완료" : "인증 미완료"}
                </span>
              )}
            </div>

            {showActionArea && (
              // 등록된 통장 사본이 있고 수정 모드가 아닐 때
              <div className="flex flex-wrap gap-2 items-center justify-start sm:justify-end">
                {showEditButton ? (
                  <Button
                    type="primary"
                    htmlType="button"
                    size="small"
                    className="bg-[#171717] hover:!bg-[#171717]"
                    onClick={() => setIsEditMode(true)}
                  >
                    수정
                  </Button>
                ) : (
                  /* 처음 등록하거나 수정 모드일 때 */
                  <>
                    {showCancelButton && (
                      <Button
                        htmlType="button"
                        size="small"
                        className="border-[#171717] text-[#171717] hover:!border-[#171717] hover:!text-[#171717]"
                        onClick={() => {
                          setIsEditMode(false);
                          setIsDisabled(true);
                          const savedBankName = userInfo.type_info.bank_name;
                          const isCustomBank =
                            !!savedBankName &&
                            !allBankValues.includes(savedBankName);
                          form.setFieldsValue({
                            bank_holder_name:
                              userInfo.type_info.bank_holder_name || null,
                            bank_name: isCustomBank
                              ? "custom"
                              : savedBankName || null,
                            custom_bank_name: isCustomBank
                              ? savedBankName
                              : null,
                            bank_account_number:
                              userInfo.type_info.bank_account_number || null,
                          });
                          setBankAccntImg(
                            userInfo?.type_info?.bank_account_url || null
                          );
                          setSelectedFile(null);
                        }}
                      >
                        취소
                      </Button>
                    )}
                    <Button
                      type="primary"
                      htmlType="button"
                      size="small"
                      className="bg-[#171717] hover:!bg-[#171717]"
                      onClick={() => form.submit()}
                    >
                      {isEditMode ? "완료" : "등록"}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        }
        className={`w-full max-w-[600px] border border-[#d9d9d9] rounded-[10px] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] mb-[30px] ${cardClassName}`}
      >
        {!hasBankAccount && isDisabled ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Typography.Text className="text-center text-gray-600">
              아직 등록된 정보가 없습니다.
              <br />
              {userInfo?.user_info.user_type === "brand" ? (
                "통장 사본을 등록해야 캠페인을 등록할 수 있습니다."
              ) : userInfo?.user_info.user_type === "agency" ? (
                "통장 사본을 등록해야 캠페인을 신청할 수 있습니다."
              ) : (
                <>
                  통장 사본을 등록해야 캠페인을 신청할 수 있습니다.
                  <br />
                  (에이전시 소속일 경우, 등록하지 않아도 됩니다.)
                </>
              )}
            </Typography.Text>

            <Button
              type="primary"
              className="bg-[#171717] hover:!bg-[#171717] mt-4"
              onClick={() => setIsDisabled(false)}
            >
              통장 사본 등록하기
            </Button>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            disabled={bankInfoDisabled}
            requiredMark={false}
            className="w-full"
            onFinish={handleSubmit}
          >
            <Form.Item
              label="예금주 명"
              name="bank_holder_name"
              rules={[{ required: true, message: "예금주 명을 입력해주세요." }]}
            >
              <Input placeholder="예금주 명을 입력해주세요." />
            </Form.Item>

            <Form.Item
              name="bank_name"
              label="은행명"
              rules={[{ required: true, message: "은행명을 선택해주세요." }]}
            >
              <Select
                placeholder="은행/증권사를 선택해주세요"
                showSearch
                optionFilterProp="label"
                virtual={false}
              >
                {bankList.map((group) => (
                  <Select.OptGroup key={group.group} label={group.group}>
                    {group.options.map((bank) => (
                      <Select.Option
                        key={bank.value}
                        value={bank.value}
                        label={bank.label}
                      >
                        {bank.label}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                ))}
                <Select.Option value="custom" label="기타(직접 입력)">
                  기타(직접 입력)
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) =>
                getFieldValue("bank_name") === "custom" ? (
                  <Form.Item
                    name="custom_bank_name"
                    preserve={false}
                    rules={[
                      {
                        required: true,
                        message: "은행명을 직접 입력해주세요.",
                      },
                    ]}
                    className="-mt-4"
                  >
                    <Input placeholder="은행명을 입력해주세요" />
                  </Form.Item>
                ) : null
              }
            </Form.Item>

            <Form.Item
              name="bank_account_number"
              label="계좌번호"
              rules={[
                { required: true, message: "계좌번호를 입력해주세요." },
                { min: 11, message: "올바른 계좌번호 형식을 입력해주세요." },
              ]}
              normalize={(value) => value.replace(/[^0-9]/g, "")}
            >
              <Input
                placeholder="숫자만 입력해주세요"
                inputMode="numeric"
                maxLength={14}
              />
            </Form.Item>
            <Form.Item
              label={lang === "en" ? "Copy of Bank Account" : "통장 사본"}
              className="mb-0"
              rules={[
                {
                  required: true,
                  message: "통장 사본을 업로드해주세요.",
                },
              ]}
            >
              <div className="flex gap-4 items-start">
                <Upload
                  listType="picture-card"
                  beforeUpload={handleFileSelect}
                  accept="image/*"
                  showUploadList={false}
                >
                  <div>
                    <UploadOutlined />
                    <div>업로드</div>
                  </div>
                </Upload>

                {(selectedFile || bankAccntImg) && (
                  <div>
                    <Image
                      src={
                        selectedFile
                          ? URL.createObjectURL(selectedFile)
                          : bankAccntImg
                      }
                      alt="통장 사본"
                      width={102}
                      height={102}
                      preview={{
                        mask: false,
                      }}
                      className="rounded-lg object-cover border border-[#d9d9d9] cursor-pointer"
                    />
                  </div>
                )}
              </div>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};
